import { MapContainer, TileLayer, GeoJSON, CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useEffect } from "react";
import { CANAL_FC, SITE_MARKERS, ENDPOINTS } from "@/lib/canalGeoJSON";

function FitBounds() {
  const map = useMap();
  useEffect(() => {
    map.fitBounds([[43.05, -0.40], [44.70, 3.80]], { padding: [30, 30] });
  }, [map]);
  return null;
}

const canalStyle = () => ({
  color: "#04151f",
  weight: 3,
  opacity: 0.9,
  lineCap: "round",
  lineJoin: "round",
});

const canalAccentStyle = () => ({
  color: "#e63946",
  weight: 1,
  opacity: 1,
  lineCap: "round",
  dashArray: "1 6",
});

export default function CanalLeafletMap({ height = "70vh" }) {
  return (
    <div className="relative border border-fg/15 overflow-hidden" style={{ height }} data-testid="canal-leaflet-map">
      <MapContainer
        center={[43.7, 1.5]}
        zoom={7}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", background: "#f4f3ef" }}
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />

        <GeoJSON data={CANAL_FC} style={canalStyle} />
        <GeoJSON data={CANAL_FC} style={canalAccentStyle} />

        {/* Terminus océan / mer */}
        {ENDPOINTS.map((p) => (
          <CircleMarker
            key={p.name}
            center={[p.lat, p.lng]}
            radius={7}
            pathOptions={{ color: "#04151f", weight: 2, fillColor: "#f4f3ef", fillOpacity: 1 }}
          >
            <Tooltip permanent direction={p.side === "atlantic" ? "left" : "right"} offset={p.side === "atlantic" ? [-8, 0] : [8, 0]} className="film-terminus-label">
              {p.side === "atlantic" ? "OCÉAN ATLANTIQUE" : "MER MÉDITERRANÉE"}<br/>
              <span style={{opacity:0.7, fontSize:'0.9em'}}>{p.name.split(' · ')[1]}</span>
            </Tooltip>
          </CircleMarker>
        ))}

        {/* 6 sites UNESCO */}
        {SITE_MARKERS.map((s) => (
          <CircleMarker
            key={s.slug}
            center={[s.lat, s.lng]}
            radius={9}
            pathOptions={{ color: "#e63946", weight: 3, fillColor: "#f4f3ef", fillOpacity: 1 }}
          >
            <Tooltip permanent direction="top" offset={[0, -12]} className="film-site-label">
              {s.name}
            </Tooltip>
            <Popup>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">Chapitre {s.chapter} · {s.km}</div>
              <div className="font-serif text-xl leading-tight mt-1">{s.name}</div>
            </Popup>
          </CircleMarker>
        ))}

        <FitBounds />
      </MapContainer>

      {/* Global Leaflet tooltip styles + minimalist editorial map filter */}
      <style>{`
        [data-testid="canal-leaflet-map"] .leaflet-tile-pane {
          filter: grayscale(1) contrast(0.72) brightness(1.16) sepia(0.06);
          opacity: 0.68;
        }
        .film-site-label.leaflet-tooltip {
          background: #f4f3ef;
          border: 1px solid #e63946;
          border-radius: 0;
          padding: 4px 8px;
          font-family: 'Cormorant Garamond', serif;
          font-size: 15px;
          font-weight: 500;
          color: #04151f;
          box-shadow: 0 2px 8px rgba(4,21,31,0.08);
          white-space: nowrap;
        }
        .film-site-label.leaflet-tooltip::before { display: none; }
        .film-terminus-label.leaflet-tooltip {
          background: #04151f;
          color: #f4f3ef;
          border: none;
          border-radius: 0;
          padding: 5px 9px;
          font-family: 'Space Mono', monospace;
          font-size: 9px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          line-height: 1.4;
          text-align: center;
          box-shadow: 0 2px 8px rgba(4,21,31,0.14);
        }
        .film-terminus-label.leaflet-tooltip::before { display: none; }
      `}</style>

      {/* Legend */}
      <div className="absolute top-4 left-4 z-[400] bg-bg/90 backdrop-blur-md border border-fg/15 p-4 font-mono text-[10px] tracking-[0.2em] uppercase text-fg/80 space-y-2 pointer-events-none">
        <div className="flex items-center gap-3">
          <span className="w-4 h-[2px] bg-fg" /> Tracé du canal
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-cinabre bg-bg" /> Site Canal'Art 2026
        </div>
        <div className="flex items-center gap-3">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-fg bg-bg" /> Terminus océan / mer
        </div>
      </div>

      {/* Corner label */}
      <div className="absolute top-4 right-4 z-[400] font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60 pointer-events-none text-right">
        <div className="font-serif text-lg leading-none normal-case tracking-tight">750 km</div>
        <div className="mt-1">Le tracé complet</div>
      </div>
    </div>
  );
}
